import React from 'react';
import { PlanningHistoryRow, TacticalMission } from './types';

interface PlanningManifestHubProps {
  selectedDate: string;
  missions: TacticalMission[];
  historyRows: PlanningHistoryRow[];
  onOpenLegManifest: (payload: {
    title: string;
    passengers: Array<{ name: string; nationality?: string; passportNumber?: string; phone?: string }>;
  }) => void;
  onOpenHistoryManifest: (row: PlanningHistoryRow) => void;
}

const PlanningManifestHub: React.FC<PlanningManifestHubProps> = ({
  selectedDate,
  missions,
  historyRows,
  onOpenLegManifest,
  onOpenHistoryManifest,
}) => {
  const legGroups = React.useMemo(() => {
    const byKey = new Map<
      string,
      {
        key: string;
        from: string;
        to: string;
        etd: string;
        eta: string;
        tail: string;
        source: 'CHARTER' | 'SCHEDULED' | 'MIXED';
        missions: TacticalMission[];
        paxTotal: number;
        passengers: Array<{ name: string; nationality?: string; passportNumber?: string; phone?: string }>;
      }
    >();

    missions.forEach((mission) => {
      const key = [mission.from || '', mission.to || '', mission.etd || '', mission.eta || '', mission.tailNumber || 'UNASSIGNED'].join('|');
      const existing = byKey.get(key);
      const missionPassengers = Array.isArray(mission.manifestPassengers) ? mission.manifestPassengers : [];
      if (!existing) {
        byKey.set(key, {
          key,
          from: mission.from || '',
          to: mission.to || '',
          etd: mission.etd || '',
          eta: mission.eta || '',
          tail: mission.tailNumber || 'UNASSIGNED',
          source: mission.source,
          missions: [mission],
          paxTotal: Number(mission.paxCount || 0),
          passengers: [...missionPassengers],
        });
        return;
      }
      existing.missions.push(mission);
      existing.paxTotal += Number(mission.paxCount || 0);
      existing.passengers.push(...missionPassengers);
      if (existing.source !== mission.source) existing.source = 'MIXED';
    });

    return Array.from(byKey.values())
      .map((g) => {
        const seen = new Set<string>();
        const dedupedPassengers = g.passengers.filter((p) => {
          const key = [p.name, p.passportNumber || '', p.phone || ''].join('|').toLowerCase();
          if (!key.trim() || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        return { ...g, passengers: dedupedPassengers };
      })
      .sort((a, b) => {
        const byTime = String(a.etd || '').localeCompare(String(b.etd || ''));
        if (byTime !== 0) return byTime;
        return `${a.from}-${a.to}`.localeCompare(`${b.from}-${b.to}`);
      });
  }, [missions]);

  const dayHistory = React.useMemo(
    () => historyRows.filter((row) => String(row.date || '').slice(0, 10) === selectedDate),
    [historyRows, selectedDate]
  );

  const sourceTone = (source: 'CHARTER' | 'SCHEDULED' | 'MIXED') => {
    if (source === 'CHARTER') return 'border-indigo-200 bg-indigo-50 text-indigo-700';
    if (source === 'SCHEDULED') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    return 'border-violet-200 bg-violet-50 text-violet-700';
  };

  const openGroupManifest = (group: (typeof legGroups)[number]) => {
    const title = `${group.tail} • ${group.from} → ${group.to} • ${selectedDate} ${group.etd || ''}`.trim();
    onOpenLegManifest({
      title,
      passengers: group.passengers,
    });
  };

  const orderedHistory = React.useMemo(() => {
    return [...dayHistory].sort((a, b) => {
      const byTail = String(a.tailNumber || '').localeCompare(String(b.tailNumber || ''));
      if (byTail !== 0) return byTail;
      return String(a.customerName || '').localeCompare(String(b.customerName || ''));
    });
  }, [dayHistory]);

  return (
    <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_380px]">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-slate-100 bg-slate-50/70 px-4 py-3">
          <p className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500">Manifest Hub</p>
          <p className="text-xl font-black text-slate-900">Operational Legs • {selectedDate}</p>
        </div>
        <div className="max-h-[70vh] overflow-y-auto custom-scrollbar p-3 space-y-2">
          {legGroups.map((group) => (
            <div key={group.key} className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.12em] border ${sourceTone(group.source)}`}>
                    {group.source}
                  </span>
                  <span className="text-xs font-black text-slate-900">{group.from} → {group.to}</span>
                </div>
                <button
                  type="button"
                  onClick={() => openGroupManifest(group)}
                  className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-indigo-700 hover:bg-indigo-100"
                >
                  Open Leg Manifest
                </button>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] font-semibold text-slate-600">
                <span>{group.etd || '--:--'} - {group.eta || '--:--'}</span>
                <span>Tail {group.tail || 'UNASSIGNED'}</span>
                <span>Total Pax {group.paxTotal}</span>
                <span>Tickets {group.missions.length}</span>
                <span>Manifest Pax {group.passengers.length}</span>
              </div>
            </div>
          ))}
          {legGroups.length === 0 && (
            <div className="rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 p-8 text-center">
              <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">No Legs Found For Selected Date</p>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-slate-100 bg-slate-50/70 px-4 py-3">
          <p className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500">Manifest History</p>
          <p className="text-sm font-black text-slate-900">{dayHistory.length} records on {selectedDate}</p>
        </div>
        <div className="max-h-[70vh] overflow-y-auto custom-scrollbar p-3 space-y-2">
          {orderedHistory.map((row) => (
            <div key={row.id} className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-black text-slate-900">{row.customerName}</span>
                <span className={`rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.12em] ${
                  row.manifestState === 'ISSUED'
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                    : 'bg-amber-50 text-amber-700 border border-amber-200'
                }`}>
                  {row.manifestState || 'PENDING'}
                </span>
              </div>
              <div className="mt-1 text-[11px] font-semibold text-slate-600">
                {row.source} • {row.tailNumber || 'UNASSIGNED'} • Pax {row.pax}
              </div>
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  onClick={() => onOpenHistoryManifest(row)}
                  className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-slate-700 hover:bg-slate-100"
                >
                  View
                </button>
              </div>
            </div>
          ))}
          {dayHistory.length === 0 && (
            <div className="rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 p-8 text-center">
              <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">No History For This Date</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanningManifestHub;
